import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { SuggestedNews } from "../../../components/elements/elements.jsx";
import "./articlePage.css";

// ─── HELPERS ──────────────────────────────────────────────────
const getImageSrc = (file) => {
  if (!file) return "";
  if (file.startsWith("http")) return file;
  return `/images/${file.replace(/^(\.\.\/)+/, "")}`;
};

const artTypeLabel = (type) =>
  type === "blog"    ? "DevLog"   :
  type === "tuto"    ? "Tutorial" :
  type === "release" ? "Release"  : "Artykuł";

// ─── GLOSSARY ─────────────────────────────────────────────────
const GlossaryText = ({ children, glossary = {} }) => {
  const [hovered, setHovered] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const process = (text) => {
    if (typeof text !== "string") return text;
    const matches = [];
    Object.keys(glossary).forEach((term) => {
      const re = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi");
      let m;
      while ((m = re.exec(text)) !== null)
        matches.push({ term: m[0], start: m.index, end: m.index + m[0].length, data: glossary[term] });
    });
    matches.sort((a, b) => a.start - b.start);
    const filtered = [];
    matches.forEach((m) => {
      if (!filtered.some((e) => m.start < e.end && m.end > e.start)) filtered.push(m);
    });
    if (!filtered.length) return text;
    const out = [];
    let last = 0;
    filtered.forEach((m, i) => {
      if (m.start > last) out.push(text.slice(last, m.start));
      out.push(
        <span
          key={i}
          className="art-gloss"
          onMouseEnter={(e) => {
            setHovered({ ...m.data, __t: m.term });
            const r = e.target.getBoundingClientRect();
            setPos({ x: r.left + r.width / 2, y: r.top - 20 });
          }}
          onMouseLeave={() => setHovered(null)}
          onClick={() => {
            if (m.data?.link) {
              m.data.link.startsWith("http")
                ? window.open(m.data.link, "_blank")
                : (window.location.href = m.data.link);
            }
          }}
        >
          {m.term}
        </span>
      );
      last = m.end;
    });
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  return (
    <span>
      {process(children)}
      {hovered && (
        <div style={{
          position: "fixed", left: pos.x, top: pos.y - 10,
          transform: "translateX(-50%) translateY(-100%)",
          zIndex: 9999, pointerEvents: "none",
        }}>
          <div className="art-gloss-tip">
            <div className="art-gloss-tip__label">{hovered.category || hovered.__t}</div>
            <div className="art-gloss-tip__text">
              {hovered.definition || hovered.desc || hovered.description || hovered.__t}
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

// ─── CODE ART ─────────────────────────────────────────────────
const CodeArt = ({ filename, label, lines = [] }) => (
  <div className="art-rel-code">
    <div className="art-rel-code-body">
      {lines.map((line, i) => (
        <span key={i} className="art-rel-code-line">
          {line.length === 0
            ? "\u00A0"
            : line.map((tok, j) => (
                <span key={j} className={`art-rel-code-tok art-rel-code-tok--${tok.type}`}>
                  {tok.text}
                </span>
              ))}
        </span>
      ))}
    </div>
    <div className="art-rel-code-footer">
      <span>{filename}</span>
      {label && <span className="art-rel-code-tag">{label}</span>}
    </div>
  </div>
);

// ─── INSTALL BLOCK ────────────────────────────────────────────
const RelInstallBlock = ({ cmd }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="art-rel-install">
      <span className="art-rel-install-cmd">
        <span className="art-rel-install-prompt">$</span>{cmd}
      </span>
      <button
        className="art-rel-install-copy"
        onClick={() => {
          navigator.clipboard?.writeText(cmd);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "✓ Skopiowano" : "Kopiuj"}
      </button>
    </div>
  );
};

// ─── TUTORIAL SLIDER (artCompType: 2) ─────────────────────────
const TutorialSlider = ({ section, glossary }) => {
  const [slide, setSlide] = useState(0);
  const tl = section.tutorialList || {};
  return (
    <>
      {tl.tutList && (
        <div className="art-steps">
          <div className="art-steps__label sec-eyebrow">{tl.tutTitle}</div>
          <div className="art-steps__list">
            {tl.tutList.map((step, si) => (
              <div key={si} className={`art-step${slide === si ? " art-step--on" : ""}`}>
                <span className="art-step__n">{String(si + 1).padStart(2, "0")}</span>
                <span className="art-step__t">{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {tl.tutImg?.length > 0 && (
        <div className="art-slider-wrap">
          <div className="art-slider">
            <div className="art-slider__track" style={{ transform: `translateX(-${100 * slide}%)` }}>
              {tl.tutImg.map((im, si) => (
                <img key={si} src={getImageSrc(im)} alt={`step ${si + 1}`} />
              ))}
            </div>
            <div className="art-slider__bar">
              <button className="art-slider__btn" onClick={() => slide > 0 && setSlide(slide - 1)}>‹</button>
              <span className="art-slider__count">{slide + 1} / {tl.tutImg.length}</span>
              <button className="art-slider__btn" onClick={() => slide < (tl.tutCont?.length ?? 1) - 1 && setSlide(slide + 1)}>›</button>
            </div>
          </div>
          <div className="art-slides-text">
            <div style={{ transform: `translateY(-${20 * slide}vh)`, transition: "transform .4s cubic-bezier(.76,0,.24,1)" }}>
              {tl.tutCont?.map((ct, si) => (
                <div key={si} className="art-slide-text"><span>{ct}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── SECTION ──────────────────────────────────────────────────
const RelSection = ({ section, idx, glossary, isLast }) => {

  // ── new format: blocks[] ──
  const renderBlock = (block, bi) => {
    switch (block.type) {
      case "text":
        return block.data.map((para, pi) => (
          <p key={`${bi}-${pi}`} className="art-rel-section-body">
            <GlossaryText glossary={glossary}>{para}</GlossaryText>
          </p>
        ));

      case "img":
        return (
          <div key={bi} className="art-section__img-wrap">
            <img src={getImageSrc(block.data.src)} alt={block.data.alt || ""} className="imgBasicWidth" />
          </div>
        );

      case "codeArt":
        return <CodeArt key={bi} {...block.data} />;

      case "componentGrid":
        return (
          <div key={bi} className="art-rel-comp-grid">
            {block.data.map((comp, ci) => (
              <div key={ci} className="art-rel-comp-card">
                <div className="art-rel-comp-icon">{comp.icon}</div>
                <div className="art-rel-comp-name">{comp.name}</div>
                <p className="art-rel-comp-desc">{comp.desc}</p>
                {comp.tag && <span className="art-rel-comp-tag">{comp.tag}</span>}
              </div>
            ))}
          </div>
        );

      case "callout":
        return (
          <div key={bi} className="art-rel-callout">
            <div className="art-rel-callout-label">{block.data.label}</div>
            <p><GlossaryText glossary={glossary}>{block.data.text}</GlossaryText></p>
          </div>
        );

      case "installBlock":
        return <RelInstallBlock key={bi} cmd={block.data.cmd} />;

      default:
        return null;
    }
  };

  // ── legacy format: artCompType 1/2, no blocks[] ──
  const renderLegacy = () => {
    if (section.artCompType === 2) {
      return (
        <>
          {section.content && (
            <p className="art-rel-section-body">
              <GlossaryText glossary={glossary}>{section.content}</GlossaryText>
            </p>
          )}
          <TutorialSlider section={section} glossary={glossary} />
        </>
      );
    }
    // artCompType: 1 or anything else
    return (
      <>
        {section.content && (
          <p className="art-rel-section-body">
            <GlossaryText glossary={glossary}>{section.content}</GlossaryText>
          </p>
        )}
        {section.img && (
          <div className="art-section__img-wrap">
            <img src={getImageSrc(section.img)} alt={section.title} className="imgBasicWidth" />
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <section className="art-rel-section" id={`art-s${idx}`} data-idx={idx}>
        <div className="art-rel-section-header">
          <span className="art-rel-section-num">SECTION {String(idx + 1).padStart(2, "0")}</span>
          {section.sectionLabel && (
            <span className="art-rel-section-label">{section.sectionLabel}</span>
          )}
        </div>
        <h2 className="art-rel-section-title">{section.title}</h2>
        {section.subtitle && (
          <p className="art-rel-section-subtitle">{section.subtitle}</p>
        )}
        {section.blocks ? section.blocks.map((block, bi) => renderBlock(block, bi)) : renderLegacy()}
      </section>

      {!isLast && (
        <div className="art-rel-divider">
          <span className="art-rel-divider-mark">◆</span>
        </div>
      )}
    </>
  );
};

// ─── ARTICLE PAGE ─────────────────────────────────────────────
const ArticlePage = ({
  news = [],
  projects = [],
  authors = [],
  announcements: _a = [],
  glossary = {},
}) => {
  const params = useParams();
  const wildcard = params["*"];
  const id = params.id || (wildcard ? wildcard.split("/")[0] : undefined);

  const [article, setArticleData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);

  useEffect(() => {
    if (!id) { setArticleData(null); return; }
    const found = (Array.isArray(news) ? news : []).find((a) => a.id.toString() === id);
    setArticleData(found || null);
  }, [id, news]);

  // Progress bar
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop || document.body.scrollTop;
      const total = doc.scrollHeight - doc.clientHeight;
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Animate-on-scroll observer (for SuggestedNews and any other .animate-on-scroll elements)
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add("animate-in");
      }),
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".animate-on-scroll").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [article]);

  // TOC active section
  useEffect(() => {
    const sections = document.querySelectorAll(".art-rel-section[data-idx]");
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSectionIdx(parseInt(e.target.dataset.idx, 10));
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    sections.forEach((s) => observer.observe(s));
    return () => sections.forEach((s) => observer.unobserve(s));
  }, [article]);

  if (!article) return <div className="art-error">Nie znaleziono artykułu</div>;

  const author = authors.find((a) => a.id === article.authorId);
  const isOwner = author?.id === 0;
  const rd = article.releaseData || {};

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article.title;
    const desc = article.content?.substring(0, 150) + "..." || "";
    const tags = article.category?.map((c) => `#${c.replace(/\s+/g, "")}`).join(" ") || "#TehaqueDev";
    const map = {
      twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${title}\n\n${desc}\n\n${tags}`)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    if (map[platform]) window.open(map[platform], "_blank", "width=600,height=400");
    else if (platform === "copy") navigator.clipboard?.writeText(url);
  };

  const renderTitle = () => {
    const { title, titleEmphasis } = article;
    if (!titleEmphasis || !title.includes(titleEmphasis)) return title;
    const parts = title.split(titleEmphasis);
    return <>{parts[0]}<em>{titleEmphasis}</em>{parts[1]}</>;
  };

  const relatedArticles = rd.relatedArticles?.length
    ? rd.relatedArticles
    : news.filter((n) => n.id !== article.id).slice(0, 3).map((n) => ({ id: n.id, title: n.title }));

  return (
    <div className="art-rel-page">
      {/* Progress bar */}
      <div className="art-rel-progress" style={{ width: `${progress}%` }} />

      {/* ── HERO ────────────────────────────────────────────── */}
      <header className="art-rel-hero">
        <div className="art-rel-breadcrumb">
          {article.category?.map((c, i) => (
            <span key={i} className="art-rel-breadcrumb__tag">{c}</span>
          ))}
          <span className="art-rel-breadcrumb__sep">·</span>
          <span className="art-rel-breadcrumb__tag">{artTypeLabel(article.articleType)}</span>
        </div>

        <div className="art-rel-hero-grid">
          <h1 className="art-rel-hero-title">{renderTitle()}</h1>

          <div className="art-rel-hero-meta">
            <div className="art-rel-meta-item">
              <span className="art-rel-meta-label">Autor</span>
              <div className="art-rel-author-block">
                <div className="art-rel-author-avatar">
                  {author?.nickname?.[0]?.toUpperCase() || "T"}
                </div>
                <div className="art-rel-author-info">
                  <span className="art-rel-author-name">
                    {author?.nickname || "tehaque"}
                    {isOwner && <span className="art-rel-author-role">OWNER</span>}
                  </span>
                  <span className="art-rel-author-date">{article.date}</span>
                </div>
              </div>
            </div>

            {article.readTime && (
              <div className="art-rel-meta-item">
                <span className="art-rel-meta-label">Czas czytania</span>
                <span className="art-rel-meta-value">{article.readTime}</span>
              </div>
            )}

            {rd.version && (
              <div className="art-rel-meta-item">
                <span className="art-rel-meta-label">Wersja</span>
                <span className="art-rel-meta-value art-rel-meta-value--accent">{rd.version}</span>
              </div>
            )}

            {(rd.packageName || rd.npmPackage) && (
              <div className="art-rel-meta-item">
                <span className="art-rel-meta-label">Projekt</span>
                <span className="art-rel-meta-value">
                  {rd.packageName || rd.npmPackage?.split("/").pop()}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="art-rel-strip">
          <div className="art-rel-strip-tags">
            {article.tags?.map((tag, i) => (
              <span key={i} className="art-rel-tag">{tag}</span>
            ))}
          </div>
          <div className="art-rel-strip-share">
            <span className="art-rel-share-label">Udostępnij</span>
            {[
              { id: "twitter",  label: "𝕏" },
              { id: "facebook", label: "f" },
              { id: "linkedin", label: "in" },
              { id: "copy",     label: "⚡" },
            ].map(({ id: pid, label }) => (
              <button key={pid} className="art-rel-share-btn" onClick={() => handleShare(pid)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ── 3-COLUMN BODY ───────────────────────────────────── */}
      <div className="art-rel-body">

        {/* LEFT: TOC */}
        <aside className="art-rel-toc-sidebar">
          <div className="art-rel-toc-sticky">
            <div className="art-rel-toc-title">Spis treści</div>
            <ul className="art-rel-toc-list">
              {article.articleData?.map((section, i) => (
                <li
                  key={i}
                  className={`art-rel-toc-item${activeSectionIdx === i ? " art-rel-toc-item--active" : ""}`}
                >
                  <a href={`#art-s${i}`}>
                    <span className="art-rel-toc-num">{String(i + 1).padStart(2, "0")}</span>
                    <span>{section.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* CENTER: Content */}
        <main className="art-rel-main">
          {article.content && (
            <p className="art-rel-intro">
              <GlossaryText glossary={glossary}>{article.content}</GlossaryText>
            </p>
          )}

          {article.articleData?.map((section, i) => (
            <RelSection
              key={i}
              section={section}
              idx={i}
              glossary={glossary}
              isLast={i === article.articleData.length - 1}
            />
          ))}
        </main>

        {/* RIGHT: Sidebar */}
        <aside className="art-rel-rsidebar">
          <div className="art-rel-rsidebar-sticky">

            {rd.npmPackage && (
              <div className="art-rel-rblock">
                <div className="art-rel-rblock-title">Projekt</div>
                <div className="art-rel-npm">
                  <div className="art-rel-npm-name">{rd.npmPackage}</div>
                  <div className="art-rel-npm-version">{rd.version} · {rd.license || "MIT"} License</div>
                  <a href={rd.npmUrl || "#"} className="art-rel-npm-btn" target="_blank" rel="noreferrer">
                    ↗ NPM Package
                  </a>
                </div>
              </div>
            )}

            {rd.stats?.length > 0 && (
              <div className="art-rel-rblock">
                <div className="art-rel-rblock-title">Stats</div>
                {rd.stats.map((s, i) => (
                  <div key={i} className="art-rel-stat">
                    <span className="art-rel-stat-val">{s.val}<span>{s.suffix}</span></span>
                    <span className="art-rel-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
            )}

            {relatedArticles.length > 0 && (
              <div className="art-rel-rblock">
                <div className="art-rel-rblock-title">Podobne artykuły</div>
                {relatedArticles.map((rel, i) => (
                  <Link key={i} to={`/article/${rel.id}`} className="art-rel-related">
                    <span className="art-rel-related-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="art-rel-related-title">{rel.title}</span>
                  </Link>
                ))}
              </div>
            )}

          </div>
        </aside>

      </div>

      <SuggestedNews currentArticleId={id} news={news} authors={authors} />
    </div>
  );
};

export default ArticlePage;
